import React, { useState, useEffect, useCallback } from 'react';
import { Box, Avatar, Button, Modal, Typography, Slider, IconButton } from '@mui/material';
import Cropper from 'react-easy-crop';
import { Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL from '../config';
import { BASE_URL } from '../config';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// withCredentials is now set globally in index.js — no need to set it here

// const DEFAULT_PIC = '/goldpetals/uploads/default-profile.png';

// // FIXED: build full image URL from relative backend path
// const buildImageUrl = (relativePath) => {
//   if (!relativePath) return DEFAULT_PIC;
//   if (relativePath.startsWith('blob:') || relativePath.startsWith('http')) return relativePath;
//   // Backend serves static files from public/ via express.static('public')
//   // image_url stored as /uploads/profile_pictures/file.jpg → served at http://localhost:5001/uploads/...
//  // return `http://localhost:5001${relativePath}`;
//  const buildImageUrl = (relativePath) => {
//   if (!relativePath) return DEFAULT_PIC;
//   if (relativePath.startsWith('blob:') || relativePath.startsWith('http')) return relativePath;

//   const BASE =
//     window.location.hostname === 'localhost'
//       ? 'http://localhost:5001'
//       : 'https://family.sellspark.in';

//   return `${BASE}${relativePath}`;
// };
// };

const DEFAULT_PIC = '/goldpetals/uploads/default-profile.png';

const getImageUrl = (path) => {
  if (!path) return DEFAULT_PIC;
  if (path.startsWith('blob:')) return path;
  if (path.startsWith('http') && !path.includes('localhost')) return path;
  if (path.startsWith('http') && path.includes('localhost')) {
    return path.replace(/http:\/\/localhost:\d+/, BASE_URL);
  }
  if (path.startsWith('/')) return `${BASE_URL}${path}`;
  return `${BASE_URL}/uploads/${path}`;
};

const ProfilePictureUpload = ({ editMode, onProfilePicUpdate, userId }) => {
  const navigate = useNavigate();
  const [profilePicUrl, setProfilePicUrl] = useState(DEFAULT_PIC);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [error, setError] = useState('');

  // Fetch existing profile picture from the database
  useEffect(() => {
    if (!userId) return;

    const fetchProfilePic = async () => {
      try {
        // Get profile picture with credentials
        const responseUser = await axios.get(`${API_BASE_URL}/users/${userId}/profile-pic`, {
          withCredentials: true
        });
        // FIXED: use buildImageUrl to construct correct full URL from relative backend path
        if (responseUser.data?.profilePicUrl) {
          setProfilePicUrl(getImageUrl(responseUser.data.profilePicUrl));
        }
      } catch (err) {
        console.error('Error fetching profile picture:', err);
        if (err.response?.status === 401) {
          navigate('/login');
        }
      }
    };

    fetchProfilePic();
  }, [userId, navigate]);

  const handleProfilePicChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const imageUrl = URL.createObjectURL(file);
      setImageSrc(imageUrl);
      setIsCropModalOpen(true);
      setError('');
    } else {
      setError('Please select a valid image file.');
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Create FormData and append the cropped image
      const formData = new FormData();
      const response = await fetch(croppedImage);
      const blob = await response.blob();
      formData.append('profile_pic', blob, 'profile.jpg');
      formData.append('user_id', userId);

      // Upload the cropped image
      const uploadResponse = await axios.post(
        `${API_BASE_URL}/user/update-profile-pic`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (uploadResponse.status === 200) {
        const serverPath = uploadResponse.data.profilePicUrl;
        setProfilePicUrl(getImageUrl(serverPath));
        setIsCropModalOpen(false);
        onProfilePicUpdate(serverPath);
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setError('Failed to upload profile picture. Please try again.');
      if (error.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const getCroppedImg = (imageSrc, croppedAreaPixels) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;
        
        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );
        
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Canvas to Blob failed'));
            return;
          }
          const url = URL.createObjectURL(blob);
          resolve(url);
        }, 'image/jpeg', 0.95);
      };
      image.onerror = (error) => {
        reject(error);
      };
    });
  };

  return (
    <Box>
      {/* Profile Picture Preview */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {profilePicUrl ? (
          // FIXED: use profilePicUrl directly (already a full URL via buildImageUrl)
          <img
            src={profilePicUrl}
            alt="Profile"
            onError={(e) => { e.target.src = DEFAULT_PIC; }}
            style={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              border: '2px solid #1976D2',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Avatar
            src={DEFAULT_PIC}
            sx={{
              width: 80,
              height: 80,
              border: '4px solid white',
              boxShadow: (theme) => `0 0 20px ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />
        )}

        {/* Upload Button beside Avatar */}
        {editMode && (
          <Button
            variant="contained"
            component="label"
            color="primary"
            sx={{
              ml: 2, 
              borderRadius: '50%', 
              width: 50, 
              height: 50, 
              minWidth: 'auto', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: 3,
              mx:-2,
            }}
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleProfilePicChange}
            />
            <Typography variant="button" sx={{ color: 'white' }}>
              +
            </Typography>
          </Button>
        )}
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {/* Crop Modal */}
      <Modal
        open={isCropModalOpen}
        onClose={() => setIsCropModalOpen(false)}
      >
        <Box sx={{ 
          position: 'relative', 
          width: '100%', 
          maxWidth: 600, 
          bgcolor: 'white', 
          p: 4, 
          mx: 'auto', 
          borderRadius: 2, 
          mt: 5,
          outline: 'none'
        }}>
          <IconButton
            aria-label="close"
            onClick={() => setIsCropModalOpen(false)}
            sx={{ position: 'absolute', top: 10, right: 10 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" sx={{ textAlign: 'center', mb: 3 }}>
            Adjust and Crop Image
          </Typography>

          <Box sx={{ position: 'relative', width: '100%', height: 300, mb: 3 }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              style={{ containerStyle: { borderRadius: '8px' } }}
            />
          </Box>

          <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: 'gray' }}>
            Use the slider below to zoom in or out for better cropping.
          </Typography>

          <Box sx={{ width: '100%', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              Zoom:
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, zoom) => setZoom(zoom)}
              sx={{ maxWidth: 300 }}
            />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {Math.round(zoom * 100)}%
            </Typography>
          </Box>

          <Button
            onClick={handleCrop}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ py: 1.5 }}
          >
            Crop and Save
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProfilePictureUpload;
